import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useEquipmentManagementStore } from "../../../store/masterDataStore";
import { EquipmentManagementType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});
export type equipmentCategoryFormData = {
  categoryName: string;
  categoryDescription: string;
};

const emptyForm = {
  equipmentName: "",
  equipmentDescription: "",
};

const EquipmentManagement: React.FC = () => {
  const {
    equipmentManagement,
    fetchAllEquipmentManagement,
    createEquipmentManagement,
    updateEquipmentManagement,
    deleteEquipmentManagement,
    fetchEquipmentManagementById,
  } = useEquipmentManagementStore();
  const [rowData, setRowData] = useState<any[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<EquipmentManagementType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode && editId) {
        response = await updateEquipmentManagement({ ...formData }, editId);
      } else {
        response = await createEquipmentManagement(formData);
      }

      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        await fetchAllEquipmentManagement();
      }
    } catch (error) {
      console.error("Error creating equipment category:", error);
    }
  };

  const handleEdit = useCallback(
    async (data: any) => {
      const res = await fetchEquipmentManagementById(data._id);
      if (res) {
        setFormData({
          equipmentName: res.equipmentName,
          equipmentDescription: res.equipmentDescription,
        });
        setEditId(data._id);
        setIsEditMode(true);
        setModalOpen(true);
      }
    },
    [fetchEquipmentManagementById],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this equipment management?",
      );
      if (confirmed) {
        await deleteEquipmentManagement(id);
      }
    },
    [deleteEquipmentManagement],
  );

  useEffect(() => {
    const resData = (equipmentManagement ?? []).map(
      (equipmentManagement: EquipmentManagementType) => {
        return {
          ...equipmentManagement,
        };
      },
    );
    setRowData(resData);
  }, [equipmentManagement]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchAllEquipmentManagement();
    };
    fetchData();
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "equipmentName",
        headerName: "Equipment Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "equipmentName",
      },
      {
        field: "createdAt",
        headerName: "Created At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "createdAt",
        valueGetter: (params: any) =>
          new Date(params.data?.createdAt).toLocaleDateString("en-US") || "NA",
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "updatedAt",
        valueGetter: (params: any) =>
          new Date(params.data?.updatedAt).toLocaleDateString("en-US") || "NA",
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: () => ({
          padding: "10px 15px 0 0",
          textAlign: "left",
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "clip",
        }),
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Equipment Management"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Equipment Management"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    [handleEdit, handleDelete],
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Equipment Management</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setFormData(emptyForm);
              setEditId(null);
              setIsEditMode(false);
              setModalOpen(true);
            }}
          >
            <Plus size={20} /> Add Equipment
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={50}
                  rowHeight={50}
                  loading={false}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Equipment Management added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative"
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode ? "Update Equipment" : "Create Equipment"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="px-6 pb-6 flex flex-col gap-6">
              {/* Equipment Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="equipmentName"
                  className="text-sm font-medium text-gray-700"
                >
                  Equipment Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="equipmentName"
                  name="equipmentName"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.equipmentName}
                  onChange={handleChange}
                  placeholder="Enter equipment name"
                  required
                  aria-required="true"
                  aria-label="Equipment Name"
                />
              </div>

              {/* Category Description */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="equipmentDescription"
                  className="text-sm font-medium text-gray-700"
                >
                  Equipment Description
                </label>
                <textarea
                  id="equipmentDescription"
                  name="equipmentDescription"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.equipmentDescription}
                  onChange={handleChange}
                  placeholder="Enter equipment description (optional)"
                  rows={3}
                  aria-label="Equipment Description"
                />
              </div>
            </div>

            {/* Form Actions Footer */}
            <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
              >
                {isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default EquipmentManagement;
